/**
 * Use Case: Auth Status
 * Business logic for checking authentication status and token management
 */

import type { LoginUserData } from "../model/AuthModel";
import type { IAuthRepository } from "../repository/IAuthRepository";
import { container, TYPES } from "../../di/AuthModule";

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  return repository.isAuthenticated();
};

/**
 * Get stored token
 */
export const getStoredToken = (): string | null => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  return repository.getStoredToken();
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (): boolean => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  return repository.isTokenExpired();
};

/**
 * Get stored user data
 */
export const getStoredUserData = (): LoginUserData | null => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  return repository.getStoredUserData();
};

/**
 * Store user data
 */
export const storeUserData = (user: LoginUserData): void => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  repository.storeUserData(user);
};

/**
 * Clear all tokens and user data
 */
export const clearToken = (): void => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  repository.clearToken();
};

/**
 * Ensure a valid token exists, throws AuthenticationError if not
 */
export const ensureValidToken = async (): Promise<string> => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  return repository.ensureValidToken();
};

/**
 * Handle 401 unauthorized response - clear token
 */
export const handleUnauthorized = (): void => {
  const repository = container.get<IAuthRepository>(TYPES.IAuthRepository);
  repository.clearToken();
};

